import React from 'react';
import { Link } from 'react-router-dom';
import { Career } from '../types';
import { PLACEHOLDER_IMAGES, toPublicUrl } from '../constants';
import { ArrowRight } from 'lucide-react';

interface CareerCardProps {
  career: Career;
}

const CareerCard: React.FC<CareerCardProps> = ({ career }) => {
  // Use VITE_BUILD_ID for cache busting. On Netlify this will be COMMIT_REF.
  const v = (import.meta as any).env?.VITE_BUILD_ID || '';
  const withV = (u: string) => (u && v ? `${u}${u.includes('?') ? '&' : '?'}v=${v}` : u);

  // Compute final src with fallbacks: 1. Index-defined, 2. Standard slug path, 3. Global placeholder
  const cardSrc = toPublicUrl(
    career?.images?.card || 
    `/images/careers/${career.slug}/card.webp`
  ) || toPublicUrl(PLACEHOLDER_IMAGES.card);
  
  const placeholderSrc = toPublicUrl(PLACEHOLDER_IMAGES.card);

  return (
    <Link 
      to={`/careers/${career.slug}`} 
      className="group flex flex-col h-full bg-brand-light border border-brand-border rounded-xl overflow-hidden hover:border-brand-cyan transition-all duration-300 hover:shadow-lg hover:shadow-brand-cyan/10"
    >
      <div className="relative w-full aspect-video overflow-hidden bg-brand-dark">
        <img 
          src={withV(cardSrc)} 
          alt={career.title} 
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            const target = e.currentTarget;
            if (target.src !== withV(placeholderSrc)) {
              target.src = withV(placeholderSrc);
            }
          }}
        />
      </div>
      
      <div className="flex flex-col flex-grow p-5">
        <div className="mb-3">
          <span className="inline-block px-2 py-1 rounded bg-brand-cyan/10 border border-brand-cyan/20 text-xs font-semibold text-brand-cyan tracking-wide uppercase">
            {career.category}
          </span>
        </div>
        <h3 className="font-heading font-semibold text-lg text-brand-white group-hover:text-brand-cyan transition-colors mb-2 line-clamp-1">
          {career.title} <span className="ml-1" role="img" aria-label="emoji">{career.emoji}</span>
        </h3>
        <p className="text-brand-grey2 text-sm leading-relaxed mb-4 line-clamp-1">
          {career.tagline || "Discover your future in " + career.category}
        </p>
        <div className="mt-auto pt-3 border-t border-brand-border/50 flex items-center text-brand-cyan text-sm font-medium">
          Explore Path <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  );
};

export default CareerCard;
